// src/components/persons/PersonsDashboard.jsx
import React, { useMemo, useState, useCallback } from "react";
import {
    Box,
    Paper,
    Typography,
    Stack,
    TextField,
    InputAdornment,
    IconButton,
    Tooltip,
    Chip,
    Snackbar,
    Alert,
    Fab,
    ToggleButton,
    ToggleButtonGroup,
    Skeleton,   
    Button,
    Avatar,
    alpha,
    Card,
    CardContent,
    Grid,
} from "@mui/material";
import {
    Add as AddIcon,
    Edit as EditIcon,
    Block as BlockIcon,
    Search as SearchIcon,
    Clear as ClearIcon,
    Refresh as RefreshIcon,
    Security as SecurityIcon,
    People as PeopleIcon,
    LocalTaxi as TaxiIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Phone as PhoneIcon,
    Email as EmailIcon,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";

import PersonForm from "./PersonForm";
import { usePersons } from "../../hooks/usePersons";
import { useAuthContext } from "../../context/AuthProvider";

const PersonsDashboard = () => {
    const [open, setOpen] = useState(false);
    const [editPersonId, setEditPersonId] = useState(null);
    const [alert, setAlert] = useState({ open: false, message: "", severity: "success" });
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("ALL");

    const { user } = useAuthContext();
    const { persons = [], isLoading, isFetching, error, deleteMutation, refetchPersons } = usePersons();

    const ADMIN_ID = Number(process.env.REACT_APP_ROL_ADMIN_ID);

    // Estadísticas
    const stats = useMemo(() => {
        const total = persons.length;
        const active = persons.filter(p => p.estreg === "A").length;
        const inactive = total - active;
        const withTaxiCode = persons.filter(p => p.perpricod).length;
        const withAddresses = persons.filter(p => p.direcciones?.length > 0).length;
        return { total, active, inactive, withTaxiCode, withAddresses };
    }, [persons]);

    const showAlert = useCallback((message, severity = "success") => {
        setAlert({ open: true, message, severity });
    }, []);

    const handleOpenCreate = useCallback(() => {
        setEditPersonId(null);
        setOpen(true);
    }, []);

    const handleOpenEdit = useCallback((personId) => {
        setEditPersonId(personId);
        setOpen(true);
    }, []);

    const handleCloseForm = useCallback((shouldRefresh = false) => {
        setOpen(false);
        setEditPersonId(null);
        if (shouldRefresh) refetchPersons();
    }, [refetchPersons]);

    const handleDeactivatePerson = useCallback(async (id) => {
        try {
            await deleteMutation.mutateAsync(id);
            showAlert("Persona desactivada con éxito");
        } catch (err) {
            showAlert(err?.response?.data?.error || "Error al desactivar la persona", "error");
        }
    }, [deleteMutation, showAlert]);

    // Filtrado
    const normalizedSearch = search.trim().toLowerCase();
    const rows = useMemo(() => {
        let data = [...persons];
        if (statusFilter === "A") data = data.filter((p) => p.estreg === "A");
        if (statusFilter === "NOT_A") data = data.filter((p) => p.estreg !== "A");
        if (normalizedSearch) {
            data = data.filter((p) => {
                const fullName = `${p.pernom || ""} ${p.perape || ""}`.toLowerCase();
                const dni = String(p.perdni || "").toLowerCase();
                const tel = String(p.pertel || "").toLowerCase();
                const email = String(p.perema || "").toLowerCase();
                return fullName.includes(normalizedSearch) || dni.includes(normalizedSearch) ||
                    tel.includes(normalizedSearch) || email.includes(normalizedSearch);
            });
        }
        return data;
    }, [persons, statusFilter, normalizedSearch]);

    // Columnas del DataGrid
    const columns = useMemo(() => [
        {
            field: "avatar",
            headerName: "",
            width: 60,
            sortable: false,
            renderCell: (params) => (
                <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main' }}>
                    {params.row?.pernom?.charAt(0) || '?'}
                </Avatar>
            ),
        },
        {
            field: "fullname",
            headerName: "Persona",
            flex: 1.2,
            renderCell: (params) => (
                <Stack spacing={0.3}>
                    <Typography fontWeight={700} variant="body2" noWrap>
                        {params.row?.pernom} {params.row?.perape}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {params.row?.pertipdoc || 'DNI'}: {params.row?.perdni || "-"}
                    </Typography>
                </Stack>
            ),
        },
        {
            field: "contacto",
            headerName: "Contacto",
            flex: 1,
            minWidth: 220,
            renderCell: (params) => (
                <Stack spacing={0.3}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        <PhoneIcon fontSize="inherit" sx={{ fontSize: 12, color: 'text.secondary' }} />
                        <Typography variant="body2" noWrap>
                            {params.row?.pertel || "Sin teléfono"}
                        </Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        <EmailIcon fontSize="inherit" sx={{ fontSize: 12, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary" noWrap>
                            {params.row?.perema || "Sin email"}
                        </Typography>
                    </Stack>
                </Stack>
            ),
        },
        {
            field: "taxi",
            headerName: "Taxi",
            width: 160,
            sortable: false,
            renderCell: (params) => {
                const hasCode = params.row?.perpricod;
                return (
                    <Stack spacing={0.5}>
                        <Chip
                            size="small"
                            icon={<SecurityIcon fontSize="small" />}
                            label={hasCode ? `Código: ${params.row.perpricod}` : "Sin código"}
                            color={hasCode ? "success" : "warning"}
                            variant="outlined"
                        />
                    </Stack>
                );
            },
        },
        {
            field: "estreg",
            headerName: "Estado",
            width: 120,
            renderCell: (params) => {
                const isActive = params.value === "A";
                return (
                    <Chip
                        size="small"
                        icon={isActive ? <CheckCircleIcon fontSize="small" /> : <CancelIcon fontSize="small" />}
                        label={isActive ? "ACTIVO" : "INACTIVO"}
                        color={isActive ? "success" : "default"}
                        variant={isActive ? "filled" : "outlined"}
                    />
                );
            },
        },
        {
            field: "actions",
            headerName: "",
            width: 100,
            sortable: false,
            filterable: false,
            align: "right",
            renderCell: (params) => {
                const person = params.row;
                const canAdmin = user?.RolID === ADMIN_ID;
                return (
                    <Stack direction="row" spacing={0.5}>
                        <Tooltip title="Editar">
                            <IconButton size="small" onClick={() => handleOpenEdit(person.perid)}>
                                <EditIcon fontSize="small" color="primary" />
                            </IconButton>
                        </Tooltip>
                        {canAdmin && person.estreg !== "I" && (
                            <Tooltip title="Desactivar">
                                <IconButton size="small" onClick={() => handleDeactivatePerson(person.perid)} disabled={deleteMutation.isPending}>
                                    <BlockIcon fontSize="small" color="error" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Stack>
                );
            },
        },
    ], [ADMIN_ID, user?.RolID, handleOpenEdit, handleDeactivatePerson, deleteMutation.isPending]);

    // Loading skeleton
    if (isLoading && !persons.length) {
        return (
            <Box sx={{ p: 2.5 }}>
                <Stack spacing={2.5}>
                    <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
                    <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 2 }} />
                    <Skeleton variant="rectangular" height={500} sx={{ borderRadius: 2 }} />
                </Stack>
            </Box>
        );
    }

    return (
        <Box sx={{ height: "100vh", display: "flex", flexDirection: "column", bgcolor: "background.default", overflow: "hidden" }}>
            {/* HEADER PRINCIPAL */}
            <Box sx={{ p: 2.5, pb: 0 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
                    <Box>
                        <Typography variant="h4" fontWeight={800} sx={{ mb: 0.5 }}>
                            Personas
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Gestión de datos personales, contacto y direcciones georreferenciadas para el servicio de taxi
                        </Typography>
                    </Box>
                    <Tooltip title="Refrescar">
                        <IconButton onClick={refetchPersons} disabled={isFetching}>
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                </Stack>

                {/* TARJETAS DE ESTADÍSTICAS */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6} md={4}>
                        <Card sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Total</Typography>
                                        <Typography variant="h4" fontWeight={700}>{stats.total}</Typography>
                                    </Box>
                                    <Avatar sx={{ bgcolor: alpha('#1976d2', 0.1), color: '#1976d2', width: 48, height: 48 }}>
                                        <PeopleIcon />
                                    </Avatar>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <Card sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Activos</Typography>
                                        <Typography variant="h4" fontWeight={700} color="success.main">{stats.active}</Typography>
                                    </Box>
                                    <Avatar sx={{ bgcolor: alpha('#4caf50', 0.1), color: '#4caf50', width: 48, height: 48 }}>
                                        <CheckCircleIcon />
                                    </Avatar>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <Card sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Con código taxi</Typography>
                                        <Typography variant="h4" fontWeight={700}>{stats.withTaxiCode}</Typography>
                                    </Box>
                                    <Avatar sx={{ bgcolor: alpha('#ff9800', 0.1), color: '#ff9800', width: 48, height: 48 }}>
                                        <SecurityIcon />
                                    </Avatar>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* BARRA DE FILTROS */}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} justifyContent="space-between" sx={{ mb: 2 }}>
                    <TextField
                        size="small"
                        placeholder="Buscar por nombre, DNI, teléfono o email..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        sx={{ width: { xs: '100%', sm: 600 } }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" color="action" />
                                </InputAdornment>
                            ),
                            endAdornment: search && (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setSearch("")} edge="end">
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />

                    <ToggleButtonGroup size="small" exclusive value={statusFilter} onChange={(_, val) => val && setStatusFilter(val)}>
                        <ToggleButton value="ALL">Todos</ToggleButton>
                        <ToggleButton value="A">Activos</ToggleButton>
                        <ToggleButton value="NOT_A">Inactivos</ToggleButton>
                    </ToggleButtonGroup>


                </Stack>
            </Box>

            {/* TABLA DE DATOS */}
            <Box sx={{ flex: 1, px: 2.5, minHeight: 0, pb: 2.5, overflow: 'auto' }}>
                {error ? (
                    <Alert severity="error" action={<Button color="inherit" size="small" onClick={refetchPersons}>Reintentar</Button>}>
                        {error.message || "Error al cargar personas"}
                    </Alert>
                ) : (
                    <Paper sx={{ height: '100%', borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                        <DataGrid
                            rows={rows}
                            columns={columns}
                            loading={isFetching}
                            getRowId={(row) => row.perid}
                            disableRowSelectionOnClick
                            rowHeight={72}
                            pageSizeOptions={[10, 25, 50, 100]}
                            initialState={{
                                pagination: { paginationModel: { pageSize: 10, page: 0 } },
                                sorting: { sortModel: [{ field: "fullname", sort: "asc" }] },
                            }}
                            sx={{
                                border: 'none',
                                '& .MuiDataGrid-cell:focus': { outline: 'none' },
                                '& .MuiDataGrid-row:hover': { bgcolor: 'action.hover', cursor: 'pointer' },
                                '& .MuiDataGrid-columnHeaders': { bgcolor: 'background.default' },
                            }}
                        />
                    </Paper>
                )}
            </Box>

            {/* FAB BUTTON */}
            <Tooltip title="Agregar persona">
                <Fab color="primary" sx={{ position: "fixed", bottom: 24, right: 24 }} onClick={handleOpenCreate}>
                    <AddIcon />
                </Fab>
            </Tooltip>

            {/* FORMULARIO MODAL */}
            <PersonForm open={open} personId={editPersonId} handleClose={handleCloseForm} onNotify={showAlert} />

            {/* NOTIFICACIONES */}
            <Snackbar open={alert.open} autoHideDuration={4000} onClose={() => setAlert((a) => ({ ...a, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
                <Alert severity={alert.severity} variant="filled" onClose={() => setAlert((a) => ({ ...a, open: false }))}>
                    {alert.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default PersonsDashboard;